import React, { useEffect, useMemo, useCallback, memo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import B2BLayout from '../../../layouts/b2b'
import {
  createSession,
  getClaimFormError,
  getClaimFormLoading,
  getVisibleFormData,
} from './selectors'
import { DefaultJsonFormContextProps } from '../../../components/v2/default-json-form/context/default-json-form-context-props'
import { getClaimData } from '../../../store/claim/action-creators'
import { HStack, VStack } from '../../../ui/Stack'
import DefaultJsonFormContext from '../../../components/v2/default-json-form/context/default-json-form-context'
import CardTabs from './ui/tabs'
import { clearClaimData } from '../../../store/claim/actions'
import { useAppDispatch } from '../../../hooks/useAppDispatch/useAppDispatch'
import { FormsHeader } from './ui/forms/form-header'
import { useSaveResult } from './hooks/useSaveResult'
import { getStagePlanData } from '../../../store/stagePlan/action-creators'
import { Modal, Typography } from 'antd'
import { hasPermission } from 'src/react-app/lib/permissions'
import { Permissions } from 'src/react-app/constants/permissions'

function ClaimPage() {
  const { id } = useParams<{ id: string }>()
  const pageTitle = useMemo(() => (id === 'new' ? 'Новая рекламация' : `Рекламация №${id}`), [id])

  const breadcrumbs = useMemo(
    () => [
      {
        href: '/claims',
        title: 'Рекламации',
      },
      {
        title: pageTitle,
      },
    ],
    [pageTitle],
  )

  const dispatch = useAppDispatch()
  const entityType = 'claim'
  const ordersFormIsLoading = useSelector(getClaimFormLoading)
  const ordersFormError = useSelector(getClaimFormError)
  const { sessionData } = useSelector(createSession)
  const canAccessClaims = hasPermission(sessionData?.permissionsWithScope, Permissions.ACCESS_CLAIMS)
  const visibleFormData = useSelector(getVisibleFormData)
  const { saveResult } = useSaveResult()
  const navigate = useNavigate()

  const handleWorkLinkClick = useCallback(
    (id: string) => {
      Modal.destroyAll()
      if (id) {
        navigate(`/claim-work/${id}`)
      }
      return undefined
    },
    [navigate],
  )

  const defaultProps = useMemo(
    (): DefaultJsonFormContextProps => ({
      entityId: id,
      entityType: entityType,
    }),
    [id],
  )

  useEffect(() => {
    let mounted = true
    if (mounted && id) {
      const getInitPage = async (id: string) => {
        await dispatch(getClaimData(id))
      }
      getInitPage(id).catch(console.error)
    }
    return () => {
      mounted = false
      dispatch(clearClaimData())
    }
  }, [id, canAccessClaims, dispatch])

  const handleNextStageOnClick = useCallback(
    async (stage: any) => {
      if (!id) return
      try {
        const result = await saveResult(id, visibleFormData, stage, true)
        if (
          result?.error &&
          result &&
          'incompleteWorks' in result &&
          result.incompleteWorks &&
          Array.isArray(result.incompleteWorks) &&
          result.incompleteWorks.length > 0
        ) {
          Modal.warning({
            title: 'Предупреждение',
            content: (
              <>
                По данной рекламации есть незавершенные работы.
                <br />
                <VStack gap="4" align="start">
                  {result.incompleteWorks.map((workId: number, key: number) => {
                    return (
                      <Typography.Link
                        key={key}
                        onClick={() => handleWorkLinkClick(String(workId))}
                        style={{ fontSize: '14px' }}
                      >
                        Перейти в карточку работ №{workId}
                      </Typography.Link>
                    )
                  })}
                </VStack>
              </>
            ),
          })
          return
        }
      } catch (error) {
        console.error('Ошибка при изменении стадии:', error)
      } finally {
        dispatch(getStagePlanData('claim', id))
      }
    },
    [id, visibleFormData, saveResult, dispatch, handleWorkLinkClick],
  )

  if (!id) return null

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle={pageTitle}>
      {ordersFormError && !ordersFormIsLoading ? (
        <HStack gap={'8'} className={'mapWrapper'}>
          При выполнении запроса возникла ошибка.
          <br />
          {ordersFormError}
          <br />
          Попробуйте перезагрузить страницу или открыть заказ повторно.
        </HStack>
      ) : (
        <DefaultJsonFormContext.Provider value={defaultProps}>
          <FormsHeader id={id} nextStageOnClick={handleNextStageOnClick} showTitle={false} />
          <CardTabs id={id} />
        </DefaultJsonFormContext.Provider>
      )}
    </B2BLayout>
  )
}

export default memo(ClaimPage)
